/**
 * Форма создания номера (manager v3 §4 — управление номерным фондом).
 * Менеджер/владелец заводит номер: тип (из реестра), № и этаж.
 *
 * ОТДЕЛЬНЫЙ ЭКРАН (НЕ инлайн-блок над списком): открывается кнопкой
 * «+ Добавить номер» в списке; родитель рендерит ТОЛЬКО эту форму (список
 * не виден). Кнопки СНИЗУ: «Создать номер» + «Назад» (canon формы —
 * [[feedback_every_form_is_a_separate_screen]]). «Назад» возвращает к списку.
 *
 * Бэк: POST /v1/rooms (CreateRoomRequest — tenant_id+type_id+number+floor),
 * гейт mgmt+housekeeper (#331). tenant_id берём из /v1/me (tenant_id UUID).
 * Список типов — внутренний реестр (useRoomTypesAdmin), имена из display_name.
 *
 * i18n: все строки русские. Мобайл 375: поля в один столбец (.hsc-stack).
 */
import { useMemo, useState } from 'react';
import { Button, Card } from '@hsc/ui';
import type { RoomTypeView } from '@hsc/types';
import { useMe } from '../../shared/api/useMe.js';
import { useCreateRoom, useRoomTypesAdmin } from './useRooms.js';

function roomTypeName(rt: RoomTypeView): string {
  return rt.display_name?.ru ?? rt.display_name?.en ?? rt.code;
}

export interface RoomCreateFormProps {
  /** Вернуться к списку (кнопка «Назад» / после создания). */
  onBack: () => void;
}

export function RoomCreateForm({ onBack }: RoomCreateFormProps): JSX.Element {
  const me = useMe();
  const tenantId = me.data?.tenant_id;
  const typesQ = useRoomTypesAdmin(tenantId);
  const create = useCreateRoom();

  const [typeId, setTypeId] = useState('');
  const [number, setNumber] = useState('');
  const [floor, setFloor] = useState('1');

  const types = useMemo(() => typesQ.data ?? [], [typesQ.data]);
  const canSubmit =
    tenantId !== undefined && typeId !== '' && number.trim().length > 0 && !create.isPending;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit || tenantId === undefined) return;
    create.mutate(
      {
        tenant_id: tenantId,
        type_id: typeId,
        number: number.trim(),
        floor: Number.parseInt(floor, 10) || 0,
      },
      { onSuccess: onBack },
    );
  };

  return (
    <section data-testid="room-create-form">
      <div className="fleet-drawer__head">
        <h1 className="hsc-page__title">Новый номер</h1>
      </div>

      <Card>
        {tenantId === undefined ? (
          <>
            <p role="alert" className="ta-muted">
              Не удалось определить отель. Обновите страницу и войдите заново.
            </p>
            <div className="room-edit__footer">
              <Button
                type="button"
                variant="secondary"
                onClick={onBack}
                data-testid="room-create-back"
              >
                Назад
              </Button>
            </div>
          </>
        ) : (
          <form className="hsc-stack" onSubmit={handleSubmit}>
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="room-type">
                Тип номера
              </label>
              <select
                id="room-type"
                className="ta-select"
                data-testid="room-create-type"
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
                required
              >
                <option value="">— выберите тип —</option>
                {types.map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {roomTypeName(rt)}
                  </option>
                ))}
              </select>
              {types.length === 0 && !typesQ.isLoading && (
                <span className="hsc-input__hint">
                  Сначала заведите тип номера на вкладке «Типы номеров».
                </span>
              )}
            </div>

            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="room-number">
                Номер комнаты
              </label>
              <input
                id="room-number"
                className="ta-input"
                data-testid="room-create-number"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="Например, 101"
                maxLength={8}
                required
              />
            </div>

            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="room-floor">
                Этаж
              </label>
              <input
                id="room-floor"
                type="number"
                className="ta-input"
                data-testid="room-create-floor"
                value={floor}
                onChange={(e) => setFloor(e.target.value)}
                min={0}
                max={50}
              />
            </div>

            {create.isError && (
              <p role="alert" className="fleet-card__err" data-testid="room-create-error">
                Не удалось создать номер. Проверьте, что № не занят, и попробуйте снова.
              </p>
            )}

            {/* Кнопки СНИЗУ: Создать + Назад. */}
            <div className="room-edit__footer">
              <Button type="submit" disabled={!canSubmit} data-testid="room-create-submit">
                {create.isPending ? 'Сохраняем…' : 'Создать номер'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onBack}
                data-testid="room-create-back"
              >
                Назад
              </Button>
            </div>
          </form>
        )}
      </Card>
    </section>
  );
}
